import * as React from 'react';
import { useState } from 'react';
import { JSSClassesProps } from 'react-jss';
import { ComponentJSSClasses } from './styles';
import {
    UiImageWithLoader,
    UiImageWithLoaderPendingMode,
} from 'sletat-uikit2/dist/js/UiImageWithLoader';
import { FullScreenCarousel } from '../../../FullScreenCarousel';
import { formatTripAdvisorPhotoUrl } from '../../../../../utils/reviews';
import S from '../../../../../stores';
import { Portal } from '../../../../../../components/common/Portal';

const SMALL_PHOTO_HEIGHT_DESKTOP = 86;
const SMALL_PHOTO_MAX_WIDTH_DESKTOP = 109;

const SMALL_PHOTO_HEIGHT_MOBILE = 62;
const SMALL_PHOTO_MAX_WIDTH_MOBILE = 80;

const DEFAULT_VISIBLE_PHOTOS = 3;

export interface ReviewGalleryProps {
    photos: string[];
    isMobile: boolean;
}

export interface StyledReviewGalleryProps
    extends JSSClassesProps<ComponentJSSClasses>,
        ReviewGalleryProps {}

export const ReviewGallery = (props: StyledReviewGalleryProps) => {
    const [isCarouselOpen, setIsCarouselOpen] = useState<boolean>(false);
    const [carouselInitialIndex, setCarouselInitialIndex] = useState<number>(0);
    const { classes, photos } = props;
    const imagesCount = photos.length;

    const SMALL_PHOTO_HEIGHT = props.isMobile
        ? SMALL_PHOTO_HEIGHT_MOBILE
        : SMALL_PHOTO_HEIGHT_DESKTOP;
    const SMALL_PHOTO_MAX_WIDTH = props.isMobile
        ? SMALL_PHOTO_MAX_WIDTH_MOBILE
        : SMALL_PHOTO_MAX_WIDTH_DESKTOP;

    const onOpenCarousel = (initialIndex?: number) => {
        setIsCarouselOpen(true);
        setCarouselInitialIndex(initialIndex || 0);
    };

    const onCarouselClose = () => {
        setIsCarouselOpen(false);
        setCarouselInitialIndex(0);
    };

    const renderPhoto = (
        index: number,
        src: string,
        height: number,
        maxWidth: number
    ): JSX.Element | null => {
        if (!imagesCount) {
            return null;
        }

        return (
            <UiImageWithLoader
                src={formatTripAdvisorPhotoUrl(src, true)}
                pendingMode={UiImageWithLoaderPendingMode.once}
                imgProps={{ alt: '' }}
                loaderStyles={{
                    width: `100%`,
                    height: `${height}px`,
                }}
            />
        );
    };

    const renderPreviewPhotosBlock = (): JSX.Element | null => {
        if (!imagesCount) {
            return null;
        }

        const moreThenVisiblePhotos = photos.length > DEFAULT_VISIBLE_PHOTOS;
        const renderedPhotos = moreThenVisiblePhotos
            ? photos.slice(0, DEFAULT_VISIBLE_PHOTOS - 1)
            : photos;

        return (
            <div className={classes.previewPhoto}>
                {renderedPhotos.map((photo, id) => {
                    return (
                        <div
                            className={`
                                ${classes.smallPhoto}
                                ${classes.zoomable}
                                ${props.isMobile ? classes.smallPhotoMobile : ''}`}
                            onClick={() => onOpenCarousel(id)}
                            key={photo}
                        >
                            {renderPhoto(id, photo, SMALL_PHOTO_HEIGHT, SMALL_PHOTO_MAX_WIDTH)}
                        </div>
                    );
                })}

                {moreThenVisiblePhotos && (
                    <div
                        className={`
                            ${classes.smallPhoto}
                            ${classes.zoomable}
                            ${props.isMobile ? classes.smallPhotoMobile : ''}`}
                        onClick={() => onOpenCarousel(2)}
                    >
                        {renderPhoto(
                            2,
                            photos[DEFAULT_VISIBLE_PHOTOS - 1],
                            SMALL_PHOTO_HEIGHT,
                            SMALL_PHOTO_MAX_WIDTH
                        )}
                        <div className={classes.blackout}>
                            <span className={classes.morePhotos}>
                                +{photos.length - renderedPhotos.length} фото
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const getImageUrl = (idx: number) => photos[idx];

    return (
        <div className={classes.main}>
            {isCarouselOpen && !!imagesCount && (
                <Portal>
                    <FullScreenCarousel
                        initialSlide={carouselInitialIndex}
                        imagesCount={imagesCount}
                        title={<>{S.hotelDetails.hotelName}</>}
                        onClose={onCarouselClose}
                        getImageByIdx={getImageUrl}
                    />
                </Portal>
            )}
            {renderPreviewPhotosBlock()}
        </div>
    );
};
